/** Platform admin: settings screen (P9, mockups/admin/settings.html). fil mirrors every key. */
export const settings = {
  title: "Platform settings",
  subtitle: "Global defaults for every tenant workspace. Tenants can override branding, retention and channels within their plan limits.",
  navLabel: "Settings sections",
  saved: "Platform settings saved · logged as platform.settings.updated",
  notAvailable: "Not available yet",
  nav: {
    general: "General",
    branding: "Branding",
    email: "Email",
    security: "Security",
    compliance: "Data & compliance",
    flags: "Feature flags",
    maintenance: "Maintenance mode",
  },
  errors: {
    required: "This field is required.",
    email: "Enter a valid email address.",
    fxRate: "Enter a rate above 0 and up to 1,000.",
    hex: "Use a 6-digit hex colour, e.g. #00aeef.",
  },
  general: {
    subtitle: "Identity and defaults for new tenants.",
    platformName: "Platform name",
    platformNameHint: "Working name. Shown in emails, the tenant console and the admin console.",
    operatedBy: "Operated by",
    supportEmail: "Support email",
    supportHotline: "Support hotline",
    region: "Default data region",
    regionHint: "New tenants' Postgres schema, S3 bucket and Redis keyspace live here.",
    timezone: "Default timezone",
    language: "Default language",
    currency: "Currency",
    fxRate: "FX rate (₱ per US$)",
    fxRateHint: "Used to show provider costs (USD) next to revenue (PHP).",
    consoleUrl: "Tenant console URL",
    copyUrl: "Copy tenant console URL",
    regions: {
      ap_southeast_1: "AWS ap-southeast-1 · Singapore",
      ap_southeast_3: "AWS ap-southeast-3 · Jakarta",
      on_prem: "On-prem (Institution, CRUD.IT AI device)",
    },
    timezones: { Asia_Manila: "Asia/Manila (PHT, UTC+8)", Asia_Singapore: "Asia/Singapore", UTC: "UTC" },
    languages: { en: "English", en_fil: "English + Filipino (auto-detect, Taglish)", fil: "Filipino" },
    currencies: { PHP: "PHP (₱)", USD: "USD ($)" },
  },
  branding: {
    subtitle: "Platform-level look for the console, auth pages and system emails.",
    overrideLead: "Tenant widgets override these.",
    overrideBody: "Each tenant sets its own widget color, avatar and name in Widget Studio. White-label also hides \"Powered by Sagot\".",
    productName: "Product name",
    tagline: "Tagline",
    logo: "Logo",
    logoHint: "SVG or PNG, at least 256px. Dark variant generated automatically.",
    replace: "Replace",
    primaryColor: "Primary color",
    colorPicker: "Pick the primary color",
    contrast: "Text on the primary color keeps contrast at {{ratio}}:1.",
    preview: "Preview · system email header",
    openConsole: "Open your console",
  },
  email: {
    senderTitle: "Email sender",
    senderSubtitle: "System emails: invites, sign-in codes, usage alerts.",
    provider: "Provider",
    providers: { resend: "Resend", sendgrid: "SendGrid", ses: "Amazon SES (ap-southeast-1)", smtp: "SMTP relay" },
    from: "From",
    replyTo: "Reply-to",
    testAddress: "Test address",
    sendTest: "Send test",
  },
  templates: {
    title: "Templates",
    new: "New template",
    template: "Template",
    channel: "Channel",
    language: "Language",
    editLabel: "Edit {{name}}",
    emptyTitle: "No message templates yet",
    emptyBody: "Create the emails and notifications Sagot sends to tenants and staff, in English and Filipino.",
    newTitle: "New template",
    editTitle: "Edit template · {{name}}",
    name: "Name",
    key: "Key",
    keyHint: "Lowercase letters, numbers, dots and underscores, e.g. tenant.welcome",
    keyError: "Use 2–64 lowercase letters, numbers, dots or underscores, starting with a letter.",
    channelsError: "Pick at least one channel.",
    subject: "Email subject",
    bodyLabel: "{{channel}} message",
    variables: "Variables:",
    sendTest: "Send test",
    save: "Save template",
    saved: "Template saved",
    channels: { email: "Email", push: "Push", in_app: "In-app" },
    locales: { en: "English", fil: "Filipino" },
  },
  security: {
    staff2fa: "Enforce 2FA for platform staff",
    staff2faHint: "TOTP or passkey required. Cannot be disabled while impersonation is enabled.",
    tenant2fa: "Require 2FA for tenant owners & admins",
    tenant2faHint: "Default for new tenants; Institution tenants may enforce SSO instead.",
    staff_session_hours: "Staff session lifetime",
    idle_timeout_minutes: "Idle timeout",
    tenant_session_days: "Tenant session lifetime",
    impersonation_max_minutes: "Impersonation max duration",
    units: {
      hours: "{{count}} hour",
      hours_other: "{{count}} hours",
      minutes: "{{count}} minute",
      minutes_other: "{{count}} minutes",
      days: "{{count}} day",
      days_other: "{{count}} days",
    },
    allowlist: "Allowed admin IPs",
    allowlistHint: "Admin console and admin API only. Tenants are not affected.",
    allowlistEmpty: "Add at least one IP range before turning the allowlist on.",
    allowlistNone: "No IP ranges yet — the admin console is reachable from any IP.",
    addTitle: "Add an allowed IP",
    cidr: "IPv4 address or CIDR",
    cidrPlaceholder: "e.g. 49.145.102.7/32",
    label: "Label",
    add: "Add",
    added: "{{cidr}} added to admin allowlist",
    removed: "{{cidr}} removed from allowlist",
    removeIp: "Remove {{cidr}}",
    invalidIp: "Enter a valid IPv4 address or CIDR",
  },
  compliance: {
    retentionTitle: "Retention defaults",
    conversations: "Conversations",
    conversationsHint: "Then deleted; analytics kept anonymized.",
    conversationOptions: { d90: "90 days", d180: "180 days", d365: "1 year", d0: "Keep (Institution)" },
    files: "Uploaded files (S3)",
    fileOptions: { until_deleted: "Until source deleted", one_year_unused: "1 year after last use" },
    traces: "Langfuse traces",
    tracesHint: "PII redacted before export.",
    days: "{{count}} day",
    days_other: "{{count}} days",
    dpaTitle: "Data Privacy Act 2012 (RA 10173)",
    npcRegistered: "NPC registered",
    npcNo: "NPC registration no.",
    npcNoHint: "Personal Information Controller/Processor registration.",
    dpoName: "Data Protection Officer",
    dpoEmail: "DPO email",
    residencyLead: "Data residency:",
    residencyBody:
      "tenant data at rest stays in the default data region. Prompts sent to external model providers may be processed abroad under zero-data-retention terms; tenants that need in-country processing can pin self-hosted models or the on-prem CRUD.IT AI device. This is disclosed in the DPA.",
  },
  flags: {
    subtitle: "Global switches and their rollout lists. Per-tenant overrides live on each tenant's page.",
    filter: "Filter flags",
    clearFilter: "Clear filter",
    stages: { beta: "Beta", experiment: "Experiment" },
    notWired: "Not wired yet",
    notWiredReason: "No code reads this flag yet, so switching it would change nothing.",
    notWiredError: "This flag isn't wired to anything yet, so it can't be changed.",
    rolloutAll: "On for every workspace",
    rolloutNone: "Off for all workspaces",
    rolloutSome: "Rollout list: {{count}} workspace",
    rolloutSome_other: "Rollout list: {{count}} workspaces",
    enabled: "{{name}} enabled",
    disabled: "{{name}} disabled",
    emptyTitle: "No flags match",
    emptyBody: "Try a different name or key.",
    rollout: {
      open: "Rollout list",
      openLabel: "Rollout list for {{name}}",
      title: "Rollout list: {{name}}",
      description: "Workspaces that get this feature while the global switch is off. Saving replaces the stored list.",
      loading: "Loading the rollout list",
      onNote: "The global switch is on, so every workspace already has this feature. The list takes effect once the switch is turned off.",
      readOnly: "Your role can see this list but not change it.",
      searchLabel: "Find a workspace",
      searchPlaceholder: "Search workspaces",
      searchHint: "Type at least {{min}} letters of a workspace name, slug or owner email.",
      searchResults: "Matching workspaces",
      searchEmptyTitle: "No workspace matches",
      searchEmptyBody: "Check the spelling or try the owner's email address.",
      add: "Add",
      addLabel: "Add {{name}} to the rollout list",
      inList: "In the list",
      listTitle: "In the rollout list",
      listCount: "{{used}} of {{max}}",
      changes: "{{added}} added, {{removed}} removed (not saved yet)",
      noChanges: "No changes",
      emptyTitle: "No workspaces yet",
      emptyBody: "Search above and add the first workspace. Until then only the global switch decides.",
      emptyBodyReadOnly: "No workspace has been added. Only the global switch decides.",
      removeLabel: "Remove {{name}} from the rollout list",
      missingWorkspace: "Workspace no longer exists",
      unnamedWorkspace: "Name not shown",
      namesLimited: "Names are looked up for the first {{count}} workspaces only.",
      full: "The list is full ({{max}} workspaces). Remove one to add another.",
      save: "Save rollout list",
      saved: "{{name}} rollout list saved: {{count}} workspace",
      saved_other: "{{name}} rollout list saved: {{count}} workspaces",
    },
  },
  maintenance: {
    subtitle: "Puts the whole platform in read-only mode. For scheduled windows use",
    scheduleLink: "System Health → Schedule maintenance",
    banner: "Banner message",
    during: "During maintenance",
    options: {
      keep_chat: "Keep chat answering (read from replica)",
      pause_ingestion: "Pause ingestion queues",
      widget_offline: "Show offline message in widgets",
      allow_staff: "Allow platform staff to sign in",
    },
    preview: "Banner preview (tenant console)",
    previewEmpty: "Your banner message appears here.",
    confirmTitle: "Turn on maintenance mode now?",
    confirmBody: "All tenant consoles become read-only and show the banner immediately. Ingestion queues pause. This is logged and the on-call channel is notified.",
    turnOn: "Turn on",
    onToast: "Maintenance mode ON · logged as platform.maintenance.enabled",
    offToast: "Maintenance mode off · tenants back to normal",
    liveLead: "Maintenance mode is ON.",
    liveBody: "Tenant consoles are read-only and show the maintenance banner.",
    noAccessTitle: "Maintenance needs system permissions",
    noAccessBody: "Only Super Admins and Platform Engineers can view or change maintenance mode.",
  },
} as const;
